import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSignIn } from "./useSignIn";
import { useTotpSignIn, usePasskeySignIn } from "./useMfaSignIn";
import { AuthShell } from "./AuthShell";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { pickFieldError } from "../lib/form";
import { useTranslation, apiErrorMessage } from "../i18n";
import { ApiError } from "../api/client";

export function SignInPage() {
  const { t } = useTranslation("auth");
  const { t: tErr } = useTranslation("errors");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  // Reason carried back from an abandoned second-factor step, shown on the
  // password form the user is returned to.
  const [mfaNotice, setMfaNotice] = useState<string | null>(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get("next") ?? "/";
  const signIn = useSignIn();
  const passkey = usePasskeySignIn();

  const goNext = () => void navigate(next, { replace: true });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setMfaNotice(null);
    signIn.mutate(
      { email, password },
      {
        onSuccess: (result) => {
          if (result.kind === "mfa") setMfaToken(result.mfaToken);
          else goNext();
        },
      },
    );
  }

  const emailError = pickFieldError(signIn.error, "email");
  const passwordError = pickFieldError(signIn.error, "password");
  const genericError =
    signIn.error && signIn.error.code !== "validation_failed"
      ? apiErrorMessage(signIn.error, tErr)
      : undefined;
  const passkeyError = passkey.error
    ? passkey.error instanceof ApiError
      ? apiErrorMessage(passkey.error, tErr)
      : t("passkeyFailed")
    : undefined;

  if (mfaToken) {
    // An expired challenge is unrecoverable at this step — the copy tells the
    // user to sign in again, so hand them back the password form rather than
    // stranding them on a code screen no code can satisfy.
    return (
      <TotpStep
        mfaToken={mfaToken}
        onDone={goNext}
        onExpired={(reason) => {
          setMfaToken(null);
          setMfaNotice(reason);
        }}
      />
    );
  }

  return (
    <AuthShell>
      <h1 className="mb-6 text-2xl font-semibold text-slate-100">{t("signInTitle")}</h1>

      {mfaNotice ? (
        <Banner kind="error" className="mb-4">
          {mfaNotice}
        </Banner>
      ) : null}
      {genericError ? (
        <Banner kind="error" className="mb-4">
          {genericError}
        </Banner>
      ) : null}
      {passkeyError ? (
        <Banner kind="error" className="mb-4">
          {passkeyError}
        </Banner>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label={t("email")} error={emailError}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hasError={!!emailError}
              {...ariaProps}
            />
          )}
        </Field>

        <Field label={t("password")} error={passwordError}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hasError={!!passwordError}
              {...ariaProps}
            />
          )}
        </Field>

        <Button type="submit" variant="primary" className="w-full" disabled={signIn.isPending}>
          {signIn.isPending ? t("signingIn") : t("signIn")}
        </Button>
      </form>

      <div className="my-4 flex items-center gap-3 text-xs text-slate-500">
        <span className="h-px flex-1 bg-slate-800" />
        {t("or")}
        <span className="h-px flex-1 bg-slate-800" />
      </div>

      <Button
        type="button"
        variant="secondary"
        className="w-full"
        disabled={passkey.isPending}
        onClick={() => {
          // Clear the expired-challenge notice too, or its banner stacks under
          // a passkey failure and the user reads two unrelated errors.
          setMfaNotice(null);
          passkey.mutate({ email }, { onSuccess: goNext });
        }}
      >
        {passkey.isPending ? t("signingIn") : t("signInPasskey")}
      </Button>

      <p className="mt-6 text-sm text-slate-400">
        <Link to="/forgot-password" className="text-arch-400 hover:underline">
          {t("forgotLink")}
        </Link>
      </p>

      <p className="mt-2 text-sm text-slate-400">
        {t("noAccount")}{" "}
        <Link to="/sign-up" className="text-arch-400 hover:underline">
          {t("createOne")}
        </Link>
      </p>
    </AuthShell>
  );
}

function TotpStep({
  mfaToken,
  onDone,
  onExpired,
}: {
  readonly mfaToken: string;
  readonly onDone: () => void;
  /** The challenge is dead — hand the reason back so sign-in can restart. */
  readonly onExpired: (reason: string) => void;
}) {
  const { t } = useTranslation("auth");
  const { t: tErr } = useTranslation("errors");
  const [code, setCode] = useState("");
  const totp = useTotpSignIn();

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    totp.mutate(
      { mfaToken, code },
      {
        onSuccess: onDone,
        onError: (err) => {
          if (err.code === "challenge_expired") onExpired(apiErrorMessage(err, tErr));
        },
      },
    );
  }

  const error = totp.error ? apiErrorMessage(totp.error, tErr) : undefined;

  return (
    <AuthShell>
      <h1 className="mb-2 text-2xl font-semibold text-slate-100">{t("totpTitle")}</h1>
      <p className="mb-6 text-sm text-slate-400">{t("totpHint")}</p>

      {error ? (
        <Banner kind="error" className="mb-4">
          {error}
        </Banner>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Field label={t("totpCodeLabel")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              {...ariaProps}
            />
          )}
        </Field>

        <Button type="submit" variant="primary" className="w-full" disabled={totp.isPending}>
          {totp.isPending ? t("signingIn") : t("verify")}
        </Button>
      </form>
    </AuthShell>
  );
}
